import type { ReactNode } from 'react'

import { cn } from '@/lib/utils'

interface BarListProps {
    items: Array<{ name: string, value: number }>
    valueFormatter?: (value: number) => string
    color?: string
    className?: string
    ariaLabel: string
}

// Ranked horizontal bars built in plain HTML, so labels never clip and it prints cleanly.
const BarList = ({ items, valueFormatter, color = 'var(--chart-1)', className, ariaLabel }: BarListProps): ReactNode => {
    const max = Math.max(...items.map(item => item.value), 1)

    return (
        <ul className={cn('grid gap-3', className)} aria-label={ariaLabel}>
            {items.map(item => (
                <li key={item.name} className='grid gap-1.5' title={`${item.name}: ${valueFormatter ? valueFormatter(item.value) : item.value}`}>
                    <div className='flex items-baseline justify-between gap-3 text-sm'>
                        <span className='truncate text-foreground'>{item.name}</span>
                        <span className='shrink-0 font-semibold text-foreground tabular-nums'>
                            {valueFormatter ? valueFormatter(item.value) : item.value}
                        </span>
                    </div>
                    <div className='h-2 overflow-hidden rounded-full bg-muted print-exact'>
                        <div
                            className='h-full rounded-full print-exact'
                            style={{ width: `${Math.max((item.value / max) * 100, 2)}%`, background: color }}
                        />
                    </div>
                </li>
            ))}
        </ul>
    )
}

export default BarList
